import { useCallback, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as lessonActions from 'Actions/lesson';
import type { AppDispatch } from 'Reducers';
import type { LessonReducer } from 'Reducers/lessonReducer';
import { eventQueue } from 'Models/EventQueue';
import { MIN_NUM_PHRASES } from 'Utils/Constants';
import { useHardwareBack } from 'Hooks/useHardwareBack';

/**
 * The lesson's Back: the header's on-screen Back, and Android's hardware or gesture Back while the
 * lesson is on screen. Both stop the lesson, then show the wrap-up after 2 or more phrases, or go
 * to the roadmap without one.
 *
 * Hardware Back has two cases the header's button cannot reach, because the wrap-up covers it:
 * - while the wrap-up is on screen it does what the wrap-up's own Back does (the roadmap), and
 *   does not ask for the wrap-up again;
 * - while the wrap-up is on its way it does nothing, as the header's button does then.
 *
 * Returns the on-screen Back.
 */
export const useLessonBack = (): (() => void) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const store = useStore<LessonReducer>();
  const completedPhrasesCount = useSelector(
    (state: LessonReducer) => state.lessonReducer.completedPhrasesCount,
  );
  // Set once Back has asked for the roadmap: a second press before the roadmap renders does not
  // navigate again.
  const leavingForRoadmapRef = useRef(false);

  const confirmBack = useCallback(() => {
    if (leavingForRoadmapRef.current) return;
    // Stop the lesson
    dispatch(lessonActions.setLessonPlaying({ lessonPlaying: false }));

    // Check if user has played at least 2 phrases
    if (completedPhrasesCount >= 2) {
      // User has played enough - show wrap up (same logic as ConfirmBack's endSession)
      dispatch(lessonActions.setLessonProcessPending(true));
      // Event queue must be clear before we initiate wrap up
      eventQueue.dispatchEvents().then(() => {
        dispatch(lessonActions.initiateWrapUp());
      });
    } else {
      // User hasn't played enough - just go back to roadmap without API request
      // Clear session data and reset state
      dispatch(lessonActions.updatePhrasesTilNextTier(MIN_NUM_PHRASES));
      dispatch(lessonActions.setTierSummary(null));
      dispatch(lessonActions.setLessonProcessPending(false));
      dispatch(lessonActions.setShowWrapUp(false));
      dispatch(lessonActions.setWrapUpIndex(0));
      dispatch(lessonActions.resetCompletedPhrasesCount());
      dispatch(
        lessonActions.updateCurrentULP({
          currentUserLevelProgress: { play_session: { id: null } },
        }),
      );
      console.log('[MUS-61] Lesson: completedPhrasesCount reset → /roadmap');
      leavingForRoadmapRef.current = true;
      navigate('/roadmap');
    }
  }, [completedPhrasesCount, dispatch, navigate]);

  const hardwareBack = useCallback(() => {
    if (leavingForRoadmapRef.current) return;
    // Read when Back is pressed: a second press can come before the first one's state renders.
    const { showWrapUp, lessonProcessPending } = store.getState().lessonReducer;
    if (showWrapUp) {
      console.log('[MUS-61] Lesson: hardware Back on the wrap-up → /roadmap');
      leavingForRoadmapRef.current = true;
      navigate('/roadmap');
    } else if (!lessonProcessPending) {
      confirmBack();
    }
  }, [confirmBack, navigate, store]);

  useHardwareBack('/lesson', hardwareBack);

  return confirmBack;
};
